import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

interface PipelineProps {
  queueTotal: number;
  queueHigh: number;
  queueMid: number;
  queueLow: number;
  workerCount: number;
  totalProcessed: number;
  providers: Record<string, { enabled: boolean; healthy: boolean; models: Array<{ id: string; default: boolean }> }>;
}

const PipelineVisualization: React.FC<PipelineProps> = ({
  queueTotal, queueHigh, queueMid, queueLow,
  workerCount, totalProcessed, providers,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>파이프라인 현황</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4 overflow-x-auto pb-2">
          {/* 큐 단계 */}
          <div className="flex-shrink-0 text-center">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 min-w-[140px]">
              <div className="text-xs text-blue-600 font-medium mb-1">대기 큐</div>
              <div className="text-2xl font-bold text-blue-700">{queueTotal}</div>
              <div className="mt-2 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-red-500">High</span>
                  <span className="font-medium">{queueHigh}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-500">Mid</span>
                  <span className="font-medium">{queueMid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Low</span>
                  <span className="font-medium">{queueLow}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 화살표 */}
          <div className="flex-shrink-0 text-gray-300 text-2xl">&#10132;</div>

          {/* 워커 단계 */}
          <div className="flex-shrink-0 text-center">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 min-w-[140px]">
              <div className="text-xs text-green-600 font-medium mb-1">Worker</div>
              <div className="text-2xl font-bold text-green-700">{workerCount}</div>
              <div className="text-xs text-gray-500 mt-1">총 {totalProcessed}건 처리</div>
            </div>
          </div>

          {/* 화살표 */}
          <div className="flex-shrink-0 text-gray-300 text-2xl">&#10132;</div>

          {/* Provider 단계 */}
          <div className="flex-shrink-0 flex gap-3">
            {Object.entries(providers).map(([name, info]) => (
              <div
                key={name}
                className={`border-2 rounded-lg p-4 min-w-[140px] text-center ${
                  info.healthy
                    ? 'bg-purple-50 border-purple-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="text-xs font-medium mb-1 capitalize text-purple-600">{name}</div>
                <Badge
                  variant={info.healthy ? 'success' : 'default'}
                  className="text-xs"
                >
                  {info.healthy ? 'Healthy' : 'Offline'}
                </Badge>
                <div className="mt-2 text-xs text-gray-500">
                  {info.models.length}개 모델
                </div>
              </div>
            ))}
            {Object.keys(providers).length === 0 && (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 min-w-[140px] text-center">
                <div className="text-xs text-gray-500">Provider 정보 없음</div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PipelineVisualization;
